import React from "react";
import { Box, Container, Heading, Text, Link, VStack } from "@chakra-ui/react";
import Header from "./Header";

const DeleteAccount: React.FC = () => {
    const primaryColor = "#1d4e4e";
    const white = "#ffffff";

    return (
        <Box>
            <Header />
            <Box bg="gray.50" minH="100vh" pt="5rem" pb={8}>
                <Container maxW="1200px">
                    <VStack gap={8} align="stretch">
                        <Box
                            bg={primaryColor}
                            p={10}
                            borderRadius="lg"
                            textAlign="center"
                            color={white}
                        >
                            <Heading as="h1" size="2xl" mb={4}>
                                Account Deletion
                            </Heading>
                            <Text fontSize="xl">Request to delete your account</Text>
                        </Box>

                        <Box
                            bg="orange.50"
                            p={4}
                            borderRadius="md"
                            borderLeft="4px"
                            borderColor="orange.500"
                        >
                            <Heading as="h2" size="md" color="#d3591c" mb={2}>
                                Important Information
                            </Heading>
                            <Text>
                                Before proceeding with account deletion, please note that this
                                action is
                                <Text as="span" fontWeight="bold" color="#1d4e4e">
                                    {" "}
                                    permanent and cannot be undone
                                </Text>
                                . All your data associated with the account will be permanently
                                deleted according to our data retention policy.
                            </Text>
                        </Box>

                        <Box
                            bg="green.50"
                            p={4}
                            borderRadius="md"
                            borderLeft="4px"
                            borderColor="green.500"
                        >
                            <Heading as="h2" size="md" color="#d3591c" mb={2}>
                                How to Request Account Deletion
                            </Heading>
                            <Text mb={4}>
                                To request the deletion of your Grade-R account, please follow these
                                steps:
                            </Text>
                            <Box
                                as="ol"
                                style={{ listStyleType: "decimal", paddingLeft: "1.5rem" }}
                            >
                                <Box as="li" mb={2}>
                                    <Text as="span" fontWeight="bold" color="#1d4e4e">
                                        Open the Grade-R App
                                    </Text>{" "}
                                    on your device
                                </Box>
                                <Box as="li" mb={2}>
                                    <Text as="span" fontWeight="bold" color="#1d4e4e">
                                        Navigate to Settings
                                    </Text>{" "}
                                    by tapping the gear icon in the top right corner
                                </Box>
                                <Box as="li" mb={2}>
                                    <Text as="span" fontWeight="bold" color="#1d4e4e">
                                        Scroll down
                                    </Text>{" "}
                                    to find and expand the "Security" section
                                </Box>
                                <Box as="li" mb={2}>
                                    <Text as="span" fontWeight="bold" color="#1d4e4e">
                                        Tap "Delete Account"
                                    </Text>{" "}
                                    and follow the confirmation prompts
                                </Box>
                                <Box as="li" mb={2}>
                                    <Text as="span" fontWeight="bold" color="#1d4e4e">
                                        Alternatively, email us
                                    </Text>{" "}
                                    at{" "}
                                    <Link href="mailto:shedeurapps@gmail.com" color="#19be3a">
                                        shedeurapps@gmail.com
                                    </Link>{" "}
                                    with the subject line "Account Deletion Request"
                                </Box>
                            </Box>
                        </Box>

                        <Box>
                            <Heading as="h2" size="md" color="#d3591c" mb={2}>
                                Data Deletion Process
                            </Heading>
                            <Text mb={4}>
                                When you request account deletion, the following data will be
                                permanently deleted:
                            </Text>
                            <Box as="ul" style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
                                <Box as="li" mb={2}>
                                    Your account information (email, name, profile details)
                                </Box>
                                <Box as="li" mb={2}>
                                    All grading data and records
                                </Box>
                                <Box as="li" mb={2}>
                                    User preferences and settings
                                </Box>
                                <Box as="li" mb={2}>
                                    Usage history and analytics data
                                </Box>
                                <Box as="li" mb={2}>
                                    Any stored feedback or notes
                                </Box>
                            </Box>
                        </Box>

                        <Box>
                            <Heading as="h2" size="md" color="#d3591c" mb={2}>
                                Data Retention
                            </Heading>
                            <Text mb={4}>
                                Some information may be retained for a limited period after account
                                deletion for the following purposes:
                            </Text>
                            <Box as="ul" style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
                                <Box as="li" mb={2}>
                                    <Text as="span" fontWeight="bold" color="#1d4e4e">
                                        Payment Records:
                                    </Text>{" "}
                                    Financial transaction records will be retained for 7 years to
                                    comply with tax and accounting regulations
                                </Box>
                                <Box as="li" mb={2}>
                                    <Text as="span" fontWeight="bold" color="#1d4e4e">
                                        Legal Requirements:
                                    </Text>{" "}
                                    Information required to comply with legal obligations may be
                                    retained as necessary
                                </Box>
                                <Box as="li" mb={2}>
                                    <Text as="span" fontWeight="bold" color="#1d4e4e">
                                        Aggregated Data:
                                    </Text>{" "}
                                    Anonymous, aggregated data that cannot be used to identify you
                                    may be retained for analytical purposes
                                </Box>
                            </Box>
                        </Box>

                        <Box>
                            <Heading as="h2" size="md" color="#d3591c" mb={2}>
                                Processing Time
                            </Heading>
                            <Text>
                                Account deletion requests via email are typically processed within 2
                                business days. You will receive a confirmation email once the
                                deletion is complete. During this period, your account will be
                                deactivated, and you will not be able to access the app.
                            </Text>
                        </Box>

                        <Box bg="gray.50" p={5} borderRadius="md" mt={4}>
                            <Heading as="h2" size="md" color="#d3591c" mb={2}>
                                Need Help?
                            </Heading>
                            <Text mb={4}>
                                If you have any questions about the account deletion process or need
                                assistance, please contact us at:
                            </Text>
                            <Text>
                                Shedeur Apps
                                <br />
                                <Link href="mailto:shedeurapps@gmail.com" color="#19be3a">
                                    shedeurapps@gmail.com
                                </Link>
                            </Text>
                        </Box>
                    </VStack>
                </Container>
            </Box>
        </Box>
    );
};

export default DeleteAccount;
